// Shared, human-readable error messages for failed API calls.
//
// Why this exists: pages historically did `catch { toast.error('Failed'); }` —
// no error binding, no console trace, and a message that tells the user nothing
// about what to do next. That is how a fully typed-out 1:1 was lost: the token
// had expired, the save 401'd, and all the user saw was a 3-second "Failed".
//
// Use this in every catch block so the message says WHY and what to do:
//
//   } catch (err) {
//     console.error('Saving X failed:', err);      // (also logged centrally in client.js)
//     setSaveError(describeError(err, 'Could not save X.'));
//   }
//
// Pair it with a PERSISTENT on-screen error (an inline banner), not just a
// toast, on any form where the user has typed more than a few words — a toast
// that vanishes in 3 seconds is not an adequate signal that their work is gone.

export const describeError = (err, fallback = 'Something went wrong.') => {
  const status = err?.response?.status;

  if (status === 401) {
    return 'Your session expired, so nothing was saved. Log in again in another tab, then come back and retry — your work on this screen is still here.';
  }
  if (status === 403) {
    return "You don't have permission to do that.";
  }
  if (status === 404) {
    return 'That record no longer exists — it may have been deleted. Reload the page.';
  }
  if (status === 413) {
    return 'That file is too large to upload.';
  }
  if (status >= 500) {
    return 'The server hit an error, so nothing was saved. Try again — if it keeps happening, send a screenshot of this message.';
  }
  if (!err?.response || err?.code === 'ERR_NETWORK') {
    return 'Could not reach the server. Check your connection and try again — your work on this screen is still here.';
  }

  // A 400-level validation error usually carries a useful server message.
  return err?.response?.data?.error || err?.message || fallback;
};

export default describeError;
